import { Grid, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import UiCard from "components/base/UiCard";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiTypography from "components/base/UiTypography";
import { ProposalPlantProduct, ProposalProduct, ProposalTransferTruck } from "generated/graphql";
import { sentenceCase } from "change-case";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import PlantProductTruckingTable from "modules/proposals/forms/subforms/PlantProductTruckingTable";
import { NUMERIC_SET_VALUE_AS } from "./formUtils";
import { computeTonsPerRound } from "./truckingUtils";

type PlantProductTruckingPanelProps = {
  plantProduct: ProposalPlantProduct;
  plantIndex: number;
  product: ProposalProduct;
};

export default function PlantProductTruckingPanel({
  plantProduct,
  plantIndex,
  product,
}: PlantProductTruckingPanelProps) {
  const { register, setValue } = useFormContext<UpdateProposalProductInputSchema>();

  const [rateOnWeight, setRateOnWeight] = useState<boolean>(plantProduct.rateOnWeight ?? false);

  const productTransferTrucks: ProposalTransferTruck[] = plantProduct.transferTrucks ?? [];

  const roundTripTime =
    plantProduct.transferTruckRoundTripTimeHrsOverride ??
    plantProduct.transferTruckRoundTripTimeHrs ??
    product.transferTruckRoundTripTime ??
    0;
  const loadBuffer = plantProduct.transferTruckLoadTimeBuffer ?? 0;
  const unloadBuffer = plantProduct.transferTruckUnloadTimeBuffer ?? 0;
  const materialNeeded = plantProduct.materialNeededOverride ?? plantProduct.materialNeeded ?? 0;

  const truckingCost = productTransferTrucks.reduce(
    (sum, t) => sum + (t.costOverride ?? t.cost ?? 0),
    0
  );
  const haulingHrs = productTransferTrucks
    .reduce((sum, t) => sum + (t.totalHaulingHours ?? 0), 0)
    .toFixed(2);
  const tonsPerRound = computeTonsPerRound(productTransferTrucks);

  const plantName = plantProduct.plant?.name ?? "Plant";
  const plantProductName = plantProduct.type ? sentenceCase(plantProduct.type) : "Product";

  const handleRateOnWeightChange = useCallback(
    (checked: boolean) => {
      setRateOnWeight(checked);
      setValue(`plantProducts.${plantIndex}.rateOnWeight` as any, checked, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    },
    [plantIndex, setValue]
  );

  return (
    <MDBox display="flex" flexDirection="column" gap="12px">
      <UiTypography uiVariant="heading-sm">
        {plantName} &mdash; {plantProductName}
      </UiTypography>

      <Grid container spacing="8px">
        <Grid item xs={12} md={4}>
          <UiFieldGroup
            label="Transfer Truck RT"
            value={String(roundTripTime)}
            inputProps={register(
              `plantProducts.${plantIndex}.transferTruckRoundTripTimeHrsOverride` as any,
              NUMERIC_SET_VALUE_AS
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <UiFieldGroup
            label="Load Buffer"
            value={String(loadBuffer)}
            inputProps={register(
              `plantProducts.${plantIndex}.transferTruckLoadTimeBuffer` as any,
              NUMERIC_SET_VALUE_AS
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <UiFieldGroup
            label="Unload Buffer"
            value={String(unloadBuffer)}
            inputProps={register(
              `plantProducts.${plantIndex}.transferTruckUnloadTimeBuffer` as any,
              NUMERIC_SET_VALUE_AS
            )}
          />
        </Grid>
      </Grid>

      <MDBox display="flex" alignItems="center" gap="8px">
        <Switch
          size="small"
          checked={rateOnWeight}
          onChange={(e) => handleRateOnWeightChange(e.target.checked)}
        />
        <UiTypography uiVariant="body-sm">Use weight to calculate trucking</UiTypography>
      </MDBox>

      <UiCard variant="summary">
        <Grid container spacing="12px">
          {[
            { label: "Trucking Cost", value: formatCentsToUSD(truckingCost) },
            { label: "Total Tons", value: Number(materialNeeded).toFixed(2) },
            { label: "Hauling Hrs", value: haulingHrs },
            { label: "Tons Per Round", value: tonsPerRound.toFixed(2) },
          ].map((item) => (
            <Grid item xs={6} md={3} key={item.label}>
              <MDBox display="flex" flexDirection="column" gap="2px">
                <UiTypography uiVariant="label-xs">{item.label}</UiTypography>
                <UiTypography uiVariant="body-sm">{item.value}</UiTypography>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </UiCard>

      <PlantProductTruckingTable
        proposalPlantProduct={{ ...plantProduct, rateOnWeight } as ProposalPlantProduct}
        productMaterialNeeded={materialNeeded}
        rateOnWeight={rateOnWeight}
      />
    </MDBox>
  );
}
